import React, { useState } from 'react'

import { Divider, Grid, Typography } from '@material-ui/core'
import { DropzoneArea } from 'material-ui-dropzone'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'

import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Paper from '@material-ui/core/Paper'

import { skuit } from 'src/services/skuit'
import { useMutation } from 'react-query'
import { AxiosError } from 'axios'
import toast from 'react-hot-toast'
import { Notification } from '../Notification'

const createData = (field: string, description: string, requirements: string) => {
  return { field, description, requirements }
}

const rows = [
  createData('product_name', 'Name of the product', 'Required'),
  createData('upc_a', 'UPC A barcode for the product', 'Required'),
  createData('sku', "The client's SKU for the product", 'Required'),
  createData('length', 'The length of 1 unit of the product in inches', 'Not required'),
  createData('width', 'The width of 1 unit of the product in inches', 'Not required'),
  createData('height', 'The height of 1 unit of the product in inches', 'Not required'),
  createData(
    'weight_lbs',
    'The weight of 1 unit of the product in lbs. Can be used in conjunction with weight_oz.',
    'Not required'
  ),
  createData(
    'weight_oz',
    'The weight of 1 unit of the product in oz. Can be used in conjunction with weight_lbs.',
    'Not required'
  ),
  createData('case_upc_a', 'UPC A barcode for a case of the product', 'Not required'),
  createData('case_count', 'The number of units of product in a single case', 'Not required'),
]

const ExpectedFormatTable = () => (
  <TableContainer component={Paper}>
    <Table aria-label="expected format">
      <TableHead>
        <TableRow>
          <TableCell>Field </TableCell>
          <TableCell align="left">Description</TableCell>
          <TableCell align="left">Requirements</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.field}>
            <TableCell component="th" scope="row">
              {row.field}
            </TableCell>
            <TableCell align="left">{row.description}</TableCell>
            <TableCell align="left">{row.requirements}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

interface BulkSkuUploadDialogProps {
  open: boolean
  clientGuid: string
  itemNameToUpload: string
  handleClose: () => void
  onSuccess: () => void
}

type ErrorResponse = {
  detail: string
}

export const BulkSkuUploadDialog = (props: BulkSkuUploadDialogProps) => {
  const { open, handleClose, itemNameToUpload: name, onSuccess, clientGuid } = props

  const [selectedFile, setSelectedFile] = useState<File | undefined>()
  const [bulkErrors, setBulkErrors] = useState<any[]>([])
  const [detailError, setDetailError] = useState<ErrorResponse | null>(null)

  const { mutate: doUpload } = useMutation(skuit.client.skus.uploadCsvSkus, {
    onSuccess: () => {
      onSuccess()
      toast.success(() => <Notification message="Successfully created Skus from csv" />)

      handleClose()
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 500) {
        setBulkErrors([])
      } else {
        if (error.response?.data.detail) {
          setDetailError(error.response.data)
        } else {
          setBulkErrors(error.response?.data)
        }
      }
      toast.error(() => <Notification message={`Failed to process CSV: ${error}`} />)
    },
  })

  const uploadCsv = (file: File) => {
    if (file) {
      setBulkErrors([])
      const data = { file }
      doUpload({ guid: clientGuid, config: { data } })
    }
  }

  return (
    <div>
      <Dialog
        fullWidth
        maxWidth="md"
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">{name} Csv Bulk Upload </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Expected Format
            <div>{ExpectedFormatTable()}</div>
            <div>
              {!!bulkErrors && !!bulkErrors.length && (
                <Grid item xs={12}>
                  <Typography color="error">Errors in CSV:</Typography>
                  {bulkErrors.map((error: any, index: number) => (
                    <Typography key={index} color="error">
                      Line {error.line}: SKU: {error.sku || '<none>'}:{' '}
                      {JSON.stringify(error.details)}
                    </Typography>
                  ))}
                </Grid>
              )}
              {!!detailError && (
                <Grid item xs={12}>
                  <Typography color="error">{detailError.detail}</Typography>
                </Grid>
              )}
            </div>
          </DialogContentText>
          <DropzoneArea
            dropzoneText="Drag and drop or click to add file"
            acceptedFiles={['.csv']}
            onChange={(loadedFiles) => {
              setSelectedFile(loadedFiles[0])
            }}
            filesLimit={1}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                if (selectedFile) {
                  uploadCsv(selectedFile)
                }
              }}
            >
              Add SKUs
            </Button>
          </div>
          <Divider variant="middle" />
        </DialogActions>
      </Dialog>
    </div>
  )
}
